import React, { useState, useRef, useEffect } from 'react';
import { Plus, Minus, Maximize2, Lock } from '../../../../images/icons/icons';

const PipelineFlowDiagram = () => {
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isLocked, setIsLocked] = useState(false);
  const containerRef = useRef(null);

  // Pipeline data
  const pipelineStages = [
    {
      id: 'initial-contact',
      name: 'Initial Contact',
      leads: 45,
      value: '₹1,25,400',
      conversionRate: '71% to next',
      color: '#3b82f6',
      x: 50,
      y: 500
    },
    {
      id: 'visit-scheduled',
      name: 'Visit Scheduled',
      leads: 32,
      value: '₹89,200',
      conversionRate: '56% to next',
      color: '#3b82f6',
      x: 300,
      y: 500
    },
    {
      id: 'visit-done',
      name: 'Visit Done',
      leads: 18,
      value: '₹67,800',
      conversionRate: '72% to closure',
      color: '#3b82f6',
      x: 550,
      y: 500
    },
    {
      id: 'closure-expected',
      name: 'Closure Expected',
      leads: 25,
      value: '₹75,600',
      conversionRate: '32% close rate',
      color: '#f59e0b',
      x: 800,
      y: 500
    },
    {
      id: 'closed-won',
      name: 'Closed Won',
      leads: 8,
      value: '₹32,400',
      conversionRate: '',
      color: '#22c55e',
      x: 1050,
      y: 500
    }
  ];

  const lostCategories = [
    {
      id: 'lost-no-interest',
      name: 'Lost - No Interest',
      leads: 13,
      value: '₹36,000',
      color: '#ef4444',
      x: 50,
      y: 200
    },
    {
      id: 'lost-no-show',
      name: 'Lost - No Show',
      leads: 14,
      value: '₹21,400',
      color: '#ef4444',
      x: 300,
      y: 200
    },
    {
      id: 'lost-price-terms',
      name: 'Lost - Price/Terms',
      leads: 7,
      value: '₹19,200',
      color: '#ef4444',
      x: 550,
      y: 200
    },
    {
      id: 'lost-final-stage',
      name: 'Lost - Final Stage',
      leads: 17,
      value: '₹43,200',
      color: '#ef4444',
      x: 800,
      y: 200
    }
  ];

  // Mouse event handlers for dragging
  const handleMouseDown = (e) => {
    if (isLocked) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || isLocked) return;
    e.preventDefault();
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (isLocked) return;
    e.preventDefault();
    
    if (e.touches.length === 1) {
      // Single touch - dragging
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    } else if (e.touches.length === 2) {
      // Two finger touch - zooming
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      setDragStart({ distance, zoom });
    }
  };

  const handleTouchMove = (e) => {
    if (isLocked) return;
    e.preventDefault();
    
    if (e.touches.length === 1 && isDragging) {
      // Single touch - dragging
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    } else if (e.touches.length === 2) {
      // Two finger touch - zooming
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      if (dragStart.distance) {
        const scale = distance / dragStart.distance;
        const newZoom = Math.max(0.5, Math.min(2, dragStart.zoom * scale));
        setZoom(newZoom);
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setDragStart({ x: 0, y: 0 });
  };

  // Prevent browser zoom
  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(prev => Math.max(0.5, Math.min(2, prev + delta)));
    }
  };

  // Zoom handlers
  const handleZoomIn = () => {
    if (!isLocked) {
      setZoom(prev => Math.min(prev + 0.1, 2));
    }
  };

  const handleZoomOut = () => {
    if (!isLocked) {
      setZoom(prev => Math.max(prev - 0.1, 0.5));
    }
  };

  const handleFitToScreen = () => {
    if (!isLocked) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleToggleLock = () => {
    setIsLocked(!isLocked);
  };

  // Add event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, isLocked]);

  // Prevent browser zoom on the container
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd, { passive: false });
      
      return () => {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isLocked, dragStart, zoom]);

  // Render pipeline stage card
  const renderPipelineCard = (stage) => (
    <g key={stage.id} transform={`translate(${stage.x}, ${stage.y})`}>
      <rect
        width="200"
        height="120"
        rx="8"
        fill={stage.color}
        stroke="#e5e7eb"
        strokeWidth="1"
        className="pipeline-card"
      />
      <text x="100" y="25" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">
        {stage.name}
      </text>
      <text x="100" y="45" textAnchor="middle" fill="white" fontSize="20" fontWeight="700">
        {stage.leads}
      </text>
      <text x="100" y="65" textAnchor="middle" fill="white" fontSize="12" fontWeight="500">
        {stage.value}
      </text>
      {stage.conversionRate && (
        <text x="100" y="85" textAnchor="middle" fill="white" fontSize="10" fontWeight="400">
          {stage.conversionRate}
        </text>
      )}
    </g>
  );

  // Render lost category card
  const renderLostCard = (lost) => (
    <g key={lost.id} transform={`translate(${lost.x}, ${lost.y})`}>
      <rect
        width="200"
        height="80"
        rx="8"
        fill={lost.color}
        stroke="#e5e7eb"
        strokeWidth="1"
        className="lost-card"
      />
      <text x="100" y="25" textAnchor="middle" fill="white" fontSize="12" fontWeight="600">
        {lost.name}
      </text>
      <text x="100" y="45" textAnchor="middle" fill="white" fontSize="16" fontWeight="700">
        {lost.leads}
      </text>
      <text x="100" y="65" textAnchor="middle" fill="white" fontSize="10" fontWeight="500">
        {lost.value}
      </text>
    </g>
  );

  // Render connection arrows
  const renderConnections = () => (
    <g className="connections">
      {/* Pipeline flow arrows */}
      <path
        d="M250 560 L300 560"
        stroke="#6b7280"
        strokeWidth="2"
        fill="none"
        markerEnd="url(#arrowhead)"
      />
      <path
        d="M500 560 L550 560"
        stroke="#6b7280"
        strokeWidth="2"
        fill="none"
        markerEnd="url(#arrowhead)"
      />
      <path
        d="M750 560 L800 560"
        stroke="#6b7280"
        strokeWidth="2"
        fill="none"
        markerEnd="url(#arrowhead)"
      />
      <path
        d="M1000 560 L1050 560"
        stroke="#6b7280"
        strokeWidth="2"
        fill="none"
        markerEnd="url(#arrowhead)"
      />
      
      {/* Lost leads arrows */}
      <path
        d="M150 240 L150 400 L50 400"
        stroke="#ef4444"
        strokeWidth="2"
        fill="none"
        strokeDasharray="5,5"
        markerEnd="url(#arrowhead-red)"
      />
      <path
        d="M400 240 L400 400 L300 400"
        stroke="#ef4444"
        strokeWidth="2"
        fill="none"
        strokeDasharray="5,5"
        markerEnd="url(#arrowhead-red)"
      />
      <path
        d="M650 240 L650 400 L550 400"
        stroke="#ef4444"
        strokeWidth="2"
        fill="none"
        strokeDasharray="5,5"
        markerEnd="url(#arrowhead-red)"
      />
      <path
        d="M900 240 L900 400 L800 400"
        stroke="#ef4444"
        strokeWidth="2"
        fill="none"
        strokeDasharray="5,5"
        markerEnd="url(#arrowhead-red)"
      />
    </g>
  );

  return (
    <div className="lms-closure-pipeline-tracker-pipeline-flow-diagram">
      <div className="lms-closure-pipeline-tracker-flow-diagram-header">
        <h3 className="lms-closure-pipeline-tracker-flow-diagram-title">Pipeline Flow Visualization</h3>
        <p className="lms-closure-pipeline-tracker-flow-diagram-subtitle">
          Interactive visual representation of lead movement patterns and conversion rates
        </p>
      </div>

      <div className="lms-closure-pipeline-tracker-flow-diagram-container">
        {/* Control Panel */}
        <div className="lms-closure-pipeline-tracker-flow-controls">
          <button 
            className={`lms-closure-pipeline-tracker-control-btn ${isLocked ? 'disabled' : ''}`}
            onClick={handleZoomIn}
            disabled={isLocked}
            title="Zoom In"
          >
            <Plus />
          </button>
          <button 
            className={`lms-closure-pipeline-tracker-control-btn ${isLocked ? 'disabled' : ''}`}
            onClick={handleZoomOut}
            disabled={isLocked}
            title="Zoom Out"
          >
            <Minus />
          </button>
          <button 
            className={`lms-closure-pipeline-tracker-control-btn ${isLocked ? 'disabled' : ''}`}
            onClick={handleFitToScreen}
            disabled={isLocked}
            title="Fit to Screen"
          >
            <Maximize2 />
          </button>
          <button 
            className={`lms-closure-pipeline-tracker-control-btn ${isLocked ? 'active' : ''}`}
            onClick={handleToggleLock}
            title={isLocked ? 'Unlock' : 'Lock'}
          >
            <Lock />
          </button>
        </div>

        {/* Flow Diagram Canvas */}
        <div 
          className="lms-closure-pipeline-tracker-flow-canvas-container"
          ref={containerRef}
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <svg
            width="100%"
            height="600"
            viewBox="-500 -500 2500 1500"
            className="lms-closure-pipeline-tracker-flow-svg"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              transformOrigin: '0 0'
            }}
          >
            {/* Arrow markers */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
              </marker>
              <marker
                id="arrowhead-red"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#ef4444" />
              </marker>
            </defs>

            {/* Background grid - fixed pattern */}
            <defs>
              <pattern 
                id="grid" 
                width="10" 
                height="10" 
                patternUnits="userSpaceOnUse"
              >
                <circle cx="5" cy="5" r="1" fill="#d1d5db" />
              </pattern>
            </defs>
            
            {/* Background rectangle that covers the entire viewport */}
            <rect 
              x="-500" 
              y="-500" 
              width="2500" 
              height="1500" 
              fill="url(#grid)" 
            />

            {/* Render connections first (behind cards) */}
            {renderConnections()}

            {/* Render pipeline stages */}
            {pipelineStages.map(renderPipelineCard)}

            {/* Render lost categories */}
            {lostCategories.map(renderLostCard)}
          </svg>
        </div>
      </div>
    </div>
  );
};

export default PipelineFlowDiagram; 